import { ClipType } from '@/types/pulse';
import { Clip } from '@/types/pulsedb';
import { Json } from '@/types/db';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getClipsForInterview = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number
  ): Promise<Clip[] | null> => {
    const query = supabase
      .from('clips')
      .select('*, conversations!inner(*)')
      .eq('conversations.interview_id', interviewId)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('created_at', { ascending: false });

    const { data } = await query.throwOnError();
    return data as unknown as Clip[] | null;
  }
);

export const getClipsForInterviewPaginated = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    starred: boolean | null = null,
    page: number = 1,
    pageSize: number = 25
  ): Promise<{ data: Clip[]; total: number }> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('clips')
      .select('*, conversations!inner(*)', { count: 'exact' })
      .eq('conversations.interview_id', interviewId)
      .eq('type', ClipType.USER_GENERATED)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (starred !== null) {
      query = query.eq('starred', starred);
    }

    const { data, count } = await query.throwOnError();
    return { data: (data as unknown as Clip[]) || [], total: count || 0 };
  }
);

export const getClip = cache(
  async (supabase: SupabaseClient, clipId: number): Promise<Clip | null> => {
    const { data } = await supabase
      .from('clips')
      .select('*')
      .eq('id', clipId)
      .eq('deleted', false)
      .single()
      .throwOnError();
    return data as unknown as Clip | null;
  }
);

export const getClipBySlug = cache(
  async (supabase: SupabaseClient, slug: string): Promise<Clip | null> => {
    const { data } = await supabase
      .from('clips')
      .select('*')
      .eq('slug', slug)
      .eq('deleted', false)
      .single()
      .throwOnError();
    return data as unknown as Clip | null;
  }
);

export const updateClip = async (
  supabase: SupabaseClient,
  clipId: number,
  data: Partial<Clip>
) => {
  const { data: clip } = await supabase
    .from('clips')
    .update(data)
    .eq('id', clipId)
    .select()
    .single()
    .throwOnError();
  return clip;
};

export const deleteClip = async (supabase: SupabaseClient, id: number) => {
  await supabase
    .from('clips')
    .update({ deleted: true })
    .eq('id', id)
    .throwOnError();
};

/** Returns the non-deleted, non-hidden clips for a conversation. */
export const getClipsForConversation = cache(
  async (supabase: SupabaseClient, conversationId: number): Promise<Clip[]> => {
    const { data } = await supabase
      .from('clips')
      .select('*')
      .eq('conversation_id', conversationId)
      .eq('deleted', false)
      .eq('hidden', false)
      .throwOnError();
    return (data as unknown as Clip[]) ?? [];
  }
);

/** Inserts a clip and returns the new row. */
export const createClip = async (
  supabase: SupabaseClient,
  data: {
    conversation_id: number;
    events: Json;
    from_start: number;
    duration: number;
    type: number;
    hidden?: boolean;
  }
): Promise<Clip> => {
  const { data: clip } = await supabase
    .from('clips')
    .insert(data)
    .select('*')
    .single()
    .throwOnError();
  return clip as unknown as Clip;
};
